/**
 * Regras do cadastro de produto. O catalogo passou a guardar marca, tipo e
 * tamanho em campos separados (em vez de um `name` que juntava tudo), porque
 * so assim da para procurar "todos os leites", corrigir a marca sem reescrever
 * o nome e comparar tamanhos. O `name` continua existindo — e o rotulo que
 * aparece nas listas — mas passa a ser derivado dos tres, salvo quando o
 * usuario escreve um nome proprio.
 */
function clean(value) {
    return (value ?? '').replace(/\s+/g, ' ').trim();
}
/** Compara ignorando caixa, acento e espaco — "1 L" e "1l" sao o mesmo tamanho. */
function fold(value) {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[\s.]/g, '')
        .replace(/,/g, '.');
}
/**
 * Monta o rotulo do produto: "Tipo Marca Tamanho" ("Leite integral Italac 1 L").
 * O tipo vem primeiro porque e por ele que o usuario procura na lista; marca e
 * tamanho entram so quando existem e ainda nao estao ditos no tipo.
 */
export function buildProductName(parts) {
    const productType = clean(parts.productType);
    const brand = clean(parts.brand);
    const size = clean(parts.size);
    const pedacos = [];
    if (productType)
        pedacos.push(productType);
    if (brand && !fold(productType).includes(fold(brand)))
        pedacos.push(brand);
    if (size && !fold(pedacos.join(' ')).includes(fold(size)))
        pedacos.push(size);
    return pedacos.join(' ').slice(0, 120);
}
/**
 * Nome final do produto: o que o usuario escreveu, se escreveu; senao o rotulo
 * montado. Nunca devolve vazio quando ha ao menos um pedaco preenchido.
 */
export function resolveProductName(typedName, parts) {
    const typed = clean(typedName);
    if (typed)
        return typed.slice(0, 120);
    return buildProductName(parts);
}
/**
 * True quando o nome exibido ainda e o gerado a partir das partes — a tela usa
 * isso para continuar atualizando o nome enquanto o usuario mexe em marca,
 * tipo e tamanho, e parar assim que ele digitar um nome proprio.
 */
export function isDerivedProductName(name, parts) {
    const atual = clean(name);
    if (!atual)
        return true;
    return fold(atual) === fold(buildProductName(parts));
}
/**
 * Embalagem com varias unidades (caixa de 20 ovos, cartela de 12). Guardar a
 * embalagem inteira como "1" impede a baixa de um ovo por vez; guardar como 20
 * unidades resolve — e e por isso que o produto sabe quantas unidades cabem
 * dentro dele.
 */
/** Quantidade a guardar no estoque ao dar entrada de `packs` embalagens. */
export function quantityFromPacks(packs, packSize) {
    if (!packSize || packSize <= 1)
        return round3(packs);
    return round3(packs * packSize);
}
/** True quando vale oferecer a entrada por embalagem em vez de por unidade. */
export function hasPack(packSize) {
    return typeof packSize === 'number' && Number.isFinite(packSize) && packSize > 1;
}
/**
 * Le o tamanho da embalagem a partir do texto impresso ("12 un", "caixa com
 * 20", "cartela 30 unidades"). Serve para a IA e o catalogo publico ja
 * deixarem o produto pronto para a baixa unitaria.
 *
 * So conta o que estiver em unidades: "500 g" e peso, nao contagem.
 */
export function parsePackSize(size) {
    if (!size)
        return null;
    const texto = size
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    // "12 un", "20 unidades", "c/ 30", "caixa com 20", "30 ovos"
    const match = /(\d+)\s*(?:un|und|unid|unidades?|ovos?|pecas?|pc)\b/.exec(texto) ??
        /\b(?:c\/|com|caixa|cartela|pacote|pct|cx|duzia|embalagem)\s*(\d+)\b/.exec(texto);
    if (!match?.[1])
        return null;
    const valor = Number(match[1]);
    if (!Number.isFinite(valor) || valor <= 1 || valor > 1000)
        return null;
    return valor;
}
function round3(value) {
    return Math.round(value * 1000) / 1000;
}
//# sourceMappingURL=product.js.map